import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Mail, RefreshCw, AlertTriangle, CheckCircle2, ShieldAlert, Clock,
} from 'lucide-react';

const API = '/api';

/**
 * EmailDeliveryStatusWidget
 * ─────────────────────────────
 * Read-only admin diagnostics for transactional email (Resend).
 * Source of truth: GET /api/admin/email/diagnostics
 *
 * Props:
 *   - variant: 'compact' (default, used in Users & Roles)
 *              | 'full'  (used in /admin/governance)
 *   - visible: boolean — when false, renders nothing (parent should role-gate
 *              first; this is just a defensive switch)
 */
export default function EmailDeliveryStatusWidget({ variant = 'compact', visible = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDiagnostics = useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/email/diagnostics`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Email diagnostics unavailable');
      if (manual) {
        toast.error('Could not refresh email diagnostics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (visible) fetchDiagnostics(false);
  }, [visible, fetchDiagnostics]);

  if (!visible) return null;

  // ── Initial load skeleton ─────────────────────────────────────
  if (loading && !data) {
    return (
      <div
        data-testid="email-diagnostics-loading"
        className="rounded-lg border border-white/10 bg-adm-surface/5 p-4 flex items-center gap-3"
      >
        <Mail className="w-4 h-4 text-white/40 animate-pulse" />
        <span className="text-sm text-white/50">Loading email delivery status…</span>
      </div>
    );
  }

  // ── Error fallback (does NOT block the page) ──────────────────
  if (error && !data) {
    return (
      <div
        data-testid="email-diagnostics-error"
        className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 text-amber-300/90 text-sm">
          <AlertTriangle className="w-4 h-4" />
          Email diagnostics unavailable
        </div>
        <Button
          data-testid="email-diagnostics-refresh-btn"
          variant="outline"
          size="sm"
          onClick={() => fetchDiagnostics(true)}
          disabled={refreshing}
          className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const isLive = data.mode === 'live';
  const totals = data.totals || data.totals_last_500 || { sent: 0, mock: 0, failed: 0 };
  const failedAttempts = (data.recent_attempts || [])
    .filter(a => (a?.metadata || {}).status === 'failed')
    .slice(0, 3);
  const hasRecentFailures = failedAttempts.length > 0;

  // ── Mode badge ────────────────────────────────────────────────
  const modeBadge = isLive ? (
    <Badge
      data-testid="email-mode-badge"
      className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
    >
      <CheckCircle2 className="w-3 h-3 mr-1" /> Live
    </Badge>
  ) : (
    <Badge
      data-testid="email-mode-badge"
      className="bg-amber-500/15 text-amber-300 border border-amber-500/30"
    >
      <ShieldAlert className="w-3 h-3 mr-1" /> Mock
    </Badge>
  );

  const failedBadge = hasRecentFailures && (
    <Badge
      data-testid="email-failed-badge"
      className="bg-rose-500/15 text-rose-300 border border-rose-500/30"
    >
      <AlertTriangle className="w-3 h-3 mr-1" /> Failures
    </Badge>
  );

  // ── COMPACT VARIANT (Users & Roles top of page) ───────────────
  if (variant === 'compact') {
    return (
      <div
        data-testid="email-diagnostics-widget-compact"
        className="rounded-lg border border-white/10 bg-adm-surface/5 p-4"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center">
              <Mail className="w-4 h-4 text-indigo-300" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white" data-testid="email-widget-title">Email Delivery</span>
                {modeBadge}
                {failedBadge}
              </div>
              <div className="text-xs text-white/50 mt-0.5" data-testid="email-widget-provider">
                {(data.provider || 'resend').toString().toUpperCase()} ·{' '}
                <span data-testid="email-widget-sender">{data.sender || '—'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <CountChip label="Sent"   value={totals.sent}   tone="emerald" testid="email-count-sent" />
            <CountChip label="Mock"   value={totals.mock}   tone="amber"   testid="email-count-mock" />
            <CountChip label="Failed" value={totals.failed} tone="rose"    testid="email-count-failed" />
            <Button
              data-testid="email-diagnostics-refresh-btn"
              variant="outline"
              size="sm"
              onClick={() => fetchDiagnostics(true)}
              disabled={refreshing}
              className="border-white/15 text-white/70 hover:bg-white/5"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Mode banner */}
        {!isLive && (
          <div
            data-testid="email-mock-banner"
            className="mt-3 text-xs text-amber-200/90 bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2"
          >
            Email delivery is currently in <strong>mock mode</strong>. Invite and reset
            links are generated, but emails are not being delivered.
          </div>
        )}
        {isLive && (
          <div
            data-testid="email-live-banner"
            className="mt-3 text-xs text-emerald-200/90 bg-emerald-500/5 border border-emerald-500/20 rounded px-3 py-2"
          >
            Email delivery is <strong>live</strong>. Invite and password reset emails
            should be delivered through Resend.
          </div>
        )}
        {hasRecentFailures && (
          <div
            data-testid="email-failure-banner"
            className="mt-2 text-xs text-rose-200/90 bg-rose-500/5 border border-rose-500/20 rounded px-3 py-2"
          >
            Recent email delivery failures detected. Check Resend API key, verified
            sender domain, or sender address.
          </div>
        )}
      </div>
    );
  }

  // ── FULL VARIANT (/admin/governance) ──────────────────────────
  return (
    <div
      data-testid="email-diagnostics-widget-full"
      className="rounded-lg border border-white/10 bg-slate-900/40 p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center mt-0.5">
            <Mail className="w-4 h-4 text-indigo-300" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-medium text-white" data-testid="email-widget-title">
                Email Delivery Diagnostics
              </h3>
              {modeBadge}
              {failedBadge}
              <Badge className="bg-slate-700 text-slate-300 text-xs">Read-only</Badge>
            </div>
            <p className="text-xs text-white/50 mt-1">
              Provider:{' '}
              <span className="text-white/80" data-testid="email-widget-provider">
                {(data.provider || 'resend').toString().toUpperCase()}
              </span>
              {' · '}Configured:{' '}
              <span
                className={data.configured ? 'text-emerald-300' : 'text-amber-300'}
                data-testid="email-widget-configured"
              >
                {data.configured ? 'yes' : 'no'}
              </span>
              {' · '}Sender:{' '}
              <span className="text-white/80" data-testid="email-widget-sender">
                {data.sender || '—'}
              </span>
            </p>
          </div>
        </div>
        <Button
          data-testid="email-diagnostics-refresh-btn"
          variant="outline"
          size="sm"
          onClick={() => fetchDiagnostics(true)}
          disabled={refreshing}
          className="border-white/15 text-white/70 hover:bg-white/5"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Sent"   value={totals.sent}   tone="emerald" testid="email-stat-sent" />
        <StatCard label="Mock"   value={totals.mock}   tone="amber"   testid="email-stat-mock" />
        <StatCard label="Failed" value={totals.failed} tone="rose"    testid="email-stat-failed" />
      </div>

      {/* Last success */}
      {data.last_success_at && (
        <div
          className="text-xs text-white/60 mb-3 flex items-center gap-2"
          data-testid="email-last-success"
        >
          <Clock className="w-3.5 h-3.5 text-emerald-400" />
          Last successful send: <span className="text-white/80">{formatTime(data.last_success_at)}</span>
        </div>
      )}

      {/* Mode banner */}
      {!isLive && (
        <div
          data-testid="email-mock-banner"
          className="mb-3 text-sm text-amber-200/90 bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2"
        >
          Email delivery is currently in <strong>mock mode</strong>. Invite and reset
          links are generated, but emails are not being delivered.
          {data.warning && <div className="text-xs text-amber-200/70 mt-1">{data.warning}</div>}
        </div>
      )}
      {isLive && (
        <div
          data-testid="email-live-banner"
          className="mb-3 text-sm text-emerald-200/90 bg-emerald-500/5 border border-emerald-500/20 rounded px-3 py-2"
        >
          Email delivery is <strong>live</strong>. Invite and password reset emails
          should be delivered through Resend.
        </div>
      )}

      {/* Recent failures */}
      {hasRecentFailures && (
        <div
          data-testid="email-failure-section"
          className="rounded border border-rose-500/20 bg-rose-500/5 p-3"
        >
          <div className="flex items-center gap-2 text-rose-300 text-sm mb-2">
            <AlertTriangle className="w-4 h-4" />
            Recent email delivery failures
          </div>
          <ul className="space-y-1.5">
            {failedAttempts.map((a, idx) => {
              const m = a.metadata || {};
              return (
                <li
                  key={idx}
                  data-testid={`email-failed-attempt-${idx}`}
                  className="text-xs text-rose-200/90 flex flex-col gap-0.5"
                >
                  <span>
                    <span className="text-rose-300/70">{formatTime(a.timestamp)}</span>{' · '}
                    {m.template || 'generic'} → <span className="text-white/80">{m.to || a.target_id || '—'}</span>
                  </span>
                  {m.error && (
                    <span className="text-rose-300/80 italic truncate">{m.error}</span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-rose-200/70">
            Check Resend API key, verified sender domain, or sender address.
          </p>
        </div>
      )}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────
const TONE = {
  emerald: 'text-emerald-300 border-emerald-500/25 bg-emerald-500/5',
  amber:   'text-amber-300 border-amber-500/25 bg-amber-500/5',
  rose:    'text-rose-300 border-rose-500/25 bg-rose-500/5',
};

function CountChip({ label, value, tone, testid }) {
  return (
    <div
      data-testid={testid}
      className={`px-2.5 py-1 rounded border text-xs flex items-center gap-1.5 ${TONE[tone]}`}
    >
      <span className="opacity-70">{label}</span>
      <span className="font-medium tabular-nums">{value ?? 0}</span>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-3 ${TONE[tone]}`}
    >
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-medium tabular-nums mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function formatTime(t) {
  if (!t) return '—';
  try {
    const d = new Date(t);
    return d.toLocaleString();
  } catch {
    return String(t);
  }
}
